<script>
  import { tick } from 'svelte';
  import Icon from './Icon.svelte';
  import { dialogState, resolveDialog, cancelDialog } from '../os/dialog.svelte.js';
  import { allFiles, createFile } from '../os/filesystem.svelte.js';
  import { extensionFor } from '../os/documents.svelte.js';

  let selectedPath = $state(null);
  let filename = $state('Untitled.txt');
  let location = $state('/Users/Guest/Documents');
  let dialogEl = $state(null);
  let lastFocus = null;

  $effect(() => {
    if (dialogState.current) {
      lastFocus = document.activeElement;
      tick().then(() => {
        const first = dialogEl?.querySelector('button, input, select, [tabindex]:not([tabindex="-1"])');
        first?.focus();
      });
    } else {
      lastFocus?.focus();
      lastFocus = null;
    }
  });

  function trapFocus(e) {
    if (e.key !== 'Tab' || !dialogEl) return;
    const focusable = dialogEl.querySelectorAll('button, input, select, [tabindex]:not([tabindex="-1"])');
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  const files = $derived.by(() => {
    const dialog = dialogState.current;
    if (!dialog) return [];
    const accept = dialog.accept ?? [];
    return allFiles().filter((file) =>
      !file.path.startsWith('/Users/Guest/.Trash/') &&
      (!accept.length || accept.includes(extensionFor(file.name)))
    );
  });

  $effect(() => {
    if (dialogState.current?.type === 'save-file') {
      filename = dialogState.current.defaultName ?? 'Untitled.txt';
      location = dialogState.current.location ?? '/Users/Guest/Documents';
    }
    selectedPath = null;
  });

  function confirm() {
    const dialog = dialogState.current;
    if (!dialog) return;
    if (dialog.type === 'open-file') {
      resolveDialog(selectedPath);
    } else if (dialog.type === 'save-file') {
      const node = createFile(location, filename, dialog.content ?? '', dialog.mimeType ?? 'text/plain');
      resolveDialog(node?.path ?? null);
    } else if (dialog.type === 'permission') {
      resolveDialog(true);
    }
  }
</script>

{#if dialogState.current}
  <div class="dialog-shade" role="presentation">
    <div class="system-dialog" role="dialog" aria-modal="true" tabindex="-1" aria-label={dialogState.current.title ?? 'AquaOS dialog'} bind:this={dialogEl} onkeydown={trapFocus}>
      <header>
        <strong>{dialogState.current.title ?? (dialogState.current.type === 'save-file' ? 'Save As' : 'Open')}</strong>
      </header>

      {#if dialogState.current.type === 'open-file'}
        <div class="dialog-file-list">
          {#each files as file (file.id)}
            <button class:selected={selectedPath === file.path} onclick={() => (selectedPath = file.path)}>
              <Icon name={file.icon} size={22} />
              <span>{file.name}</span>
              <small>{file.path}</small>
            </button>
          {:else}
            <p>No matching documents.</p>
          {/each}
        </div>
      {:else if dialogState.current.type === 'save-file'}
        <label>
          Save As
          <input bind:value={filename} />
        </label>
        <label>
          Where
          <select bind:value={location}>
            <option value="/Users/Guest/Documents">Documents</option>
            <option value="/Users/Guest/Desktop">Desktop</option>
            <option value="/Users/Guest/Downloads">Downloads</option>
          </select>
        </label>
      {:else if dialogState.current.type === 'choose-app'}
        <p>No default application is registered for <strong>{dialogState.current.file?.name}</strong>.</p>
        <div class="dialog-app-list">
          {#each dialogState.current.apps ?? [] as app}
            <button onclick={() => resolveDialog(app.id)}>
              <Icon name={app.icon} size={34} />
              <span>{app.label}</span>
            </button>
          {/each}
        </div>
      {:else if dialogState.current.type === 'confirm'}
        <div class="confirm-dialog">
          <p>{dialogState.current.message ?? 'Are you sure?'}</p>
          <div class="confirm-actions">
            <button onclick={() => resolveDialog(false)}>Cancel</button>
            <button class="danger" onclick={() => resolveDialog(true)}>{dialogState.current.confirmLabel ?? 'OK'}</button>
          </div>
        </div>
      {:else if dialogState.current.type === 'permission'}
        <div class="permission-dialog">
          <div class="permission-icon">{dialogState.current.permission === 'camera' ? '📷' : dialogState.current.permission === 'screen-capture' ? '🖥' : '🎤'}</div>
          <strong>"{dialogState.current.permission}" Access</strong>
          <p>AquaOS would like to access your {dialogState.current.permission === 'screen-capture' ? 'screen' : dialogState.current.permission}.</p>
          <div class="permission-actions">
            <button onclick={() => resolveDialog(false)}>Deny</button>
            <button class="primary" onclick={() => resolveDialog(true)}>Allow</button>
          </div>
        </div>
      {/if}

      {#if dialogState.current?.type === 'choose-app'}
        <footer><button onclick={cancelDialog}>Cancel</button></footer>
      {:else if dialogState.current?.type !== 'permission' && dialogState.current?.type !== 'confirm'}
        <footer>
          <button onclick={cancelDialog}>Cancel</button>
          <button class="primary" disabled={dialogState.current.type === 'open-file' && !selectedPath} onclick={confirm}>
            {dialogState.current.type === 'save-file' ? 'Save' : 'Open'}
          </button>
        </footer>
      {/if}
    </div>
  </div>
{/if}

<style>
  .dialog-shade {
    position: fixed;
    inset: 0;
    z-index: 20000;
    display: grid;
    place-items: center;
    background: rgba(0, 0, 0, 0.34);
  }
  .system-dialog {
    width: min(520px, calc(100vw - 32px));
    max-height: min(560px, calc(100vh - 48px));
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 16px;
    color: #edf1f3;
    background: #262b2c;
    border: 1px solid #0d1011;
    box-shadow: 0 18px 60px rgba(0,0,0,.65), inset 0 1px rgba(255,255,255,.12);
  }
  header { font-size: 15px; }
  label { display: grid; grid-template-columns: 80px 1fr; align-items: center; gap: 10px; font-size: 12px; }
  input, select {
    min-width: 0;
    padding: 7px 9px;
    color: #f5f5f5;
    background: #15191a;
    border: 1px solid #4d5658;
  }
  .dialog-file-list {
    min-height: 260px;
    overflow: auto;
    background: #141819;
    border: 1px solid #080a0b;
  }
  .dialog-file-list button {
    width: 100%;
    display: grid;
    grid-template-columns: 28px minmax(100px, 1fr) minmax(130px, 1.5fr);
    align-items: center;
    gap: 8px;
    padding: 7px 9px;
    color: #dfe4e6;
    text-align: left;
    background: transparent;
    border: 0;
    border-bottom: 1px solid #242a2b;
  }
  .dialog-file-list button.selected { background: #2b69a6; color: #fff; }
  small { overflow: hidden; color: #899496; text-overflow: ellipsis; white-space: nowrap; }
  .dialog-app-list { display: flex; gap: 10px; }
  .dialog-app-list button { display: grid; justify-items: center; gap: 5px; padding: 12px; color: #fff; background: #171b1c; border: 1px solid #454e50; }
  footer { display: flex; justify-content: flex-end; gap: 8px; }
  footer button { min-width: 76px; padding: 6px 12px; }
  footer .primary { color: #fff; background: #2875bc; border-color: #4d9be0; }
  .permission-dialog { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 20px 0; text-align: center; }
  .permission-icon { font-size: 48px; }
  .permission-dialog p { color: #b8bebf; font-size: 12px; }
  .permission-actions { display: flex; gap: 10px; }
  .permission-actions button { min-width: 80px; padding: 6px 14px; color: #ddd; background: #3a3d3e; border: 1px solid #111; }
  .permission-actions .primary { color: #fff; background: #2875bc; border-color: #4d9be0; }
  .confirm-dialog { display: flex; flex-direction: column; align-items: center; gap: 16px; padding: 24px 0; text-align: center; }
  .confirm-dialog p { font-size: 13px; color: #e0e4e5; }
  .confirm-actions { display: flex; gap: 10px; }
  .confirm-actions button { min-width: 80px; padding: 6px 14px; color: #ddd; background: #3a3d3e; border: 1px solid #111; }
  .confirm-actions .danger { color: #fff; background: #b13e3e; border-color: #8a2a2a; }
</style>
